const API_BASE = (
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? "http://localhost:3000/api" : "/api")
).replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type QueryValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | ReadonlyArray<string | number | boolean>;

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  query?: Record<string, QueryValue>;
  token?: string | null;
};

type AuthHandlers = {
  onRefresh?: (token: string) => void;
  onAuthFailure?: () => void;
};

let authHandlers: AuthHandlers = {};

export function registerAuthHandlers(handlers: AuthHandlers): void {
  authHandlers = handlers;
}

let refreshPromise: Promise<string> | null = null;

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const isAbsolute = API_BASE.startsWith("http");
  const base = isAbsolute ? API_BASE : window.location.origin + API_BASE;
  const url = new URL(`${base}${path.startsWith("/") ? path : `/${path}`}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item === undefined || item === null || item === "") continue;
          url.searchParams.append(key, String(item));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

async function refreshAccessToken(): Promise<string> {
  const data = await apiFetch<{ accessToken: string }>("/auth/refresh", {
    method: "POST",
  });
  if (!data?.accessToken) {
    throw new ApiError("Refresh response missing access token", 500);
  }
  return data.accessToken;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

function isAuthExpiredError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

// Shared refresh: all concurrent 401s wait on a single in-flight refresh.
// Transient failures (abort, network, 5xx) get one retry inside this scope so
// that a brief cold-start hiccup doesn't surface as an error to every caller.
export function getOrStartRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      return await refreshAccessToken();
    } catch (err) {
      if (isAuthExpiredError(err)) throw err;
      // Transient — give it one more shot before giving up.
      return await refreshAccessToken();
    }
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function shouldAttemptRefresh(path: string): boolean {
  return !path.startsWith("/auth/");
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions & { _retried?: boolean } = {},
): Promise<T> {
  const { body, query, headers, token, _retried, ...rest } = options;

  const finalHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  if (import.meta.env.DEV) {
    const url = buildUrl(path, query);
    const redactedAuth = finalHeaders.Authorization
      ? `Bearer ${finalHeaders.Authorization.slice(7, 17)}…`
      : "(none)";
    // eslint-disable-next-line no-console
    console.debug("[apiFetch]", rest.method ?? "GET", url, {
      ...finalHeaders,
      Authorization: redactedAuth,
    });
  }

  const res = await fetch(buildUrl(path, query), {
    ...rest,
    credentials: "include",
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !_retried && shouldAttemptRefresh(path)) {
    try {
      const newToken = await getOrStartRefresh();
      authHandlers.onRefresh?.(newToken);
      return apiFetch<T>(path, { ...options, token: newToken, _retried: true });
    } catch (err) {
      // Only logout when the refresh server explicitly says the session is dead.
      // AbortError / network / 5xx are transient — surface them to the caller
      // without nuking the session, so a cold-start blip doesn't auto-signout.
      if (isAuthExpiredError(err)) {
        authHandlers.onAuthFailure?.();
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/login")
        ) {
          window.location.href = "/login";
        }
        throw err;
      }
      if (isAbortError(err)) throw err;
      throw err instanceof ApiError
        ? err
        : new ApiError("Token refresh failed", 0, err);
    }
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${res.status})`;
    throw new ApiError(message, res.status, payload);
  }

  return payload as T;
}
